import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import StatusToggle from "../../../components/StatusToggle";
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { todayInUAEString } from '../../../utils/timezone';

export const formatDiscount = (promo) =>
    promo.discount_type === 'percentage'
        ? `${promo.discount_value}%${promo.max_discount_amount != null ? ` (max AED ${promo.max_discount_amount})` : ''}`
        : `AED ${promo.discount_value}`;

// Derived from the dates — a code can be switched on yet not usable right now.
const validityBadge = (promo) => {
    const now = moment();
    if (promo.start_date && now.isBefore(promo.start_date)) return <span className="badge badge-light-info">Scheduled</span>;
    if (promo.expiry_date && now.isAfter(promo.expiry_date)) return <span className="badge badge-light-danger">Expired</span>;
    return <span className="badge badge-light-success">Running</span>;
};

// Dates are whole UAE days (see promoCodeController.createOrUpdate).
const formatDate = (date) => (date ? moment(todayInUAEString(new Date(date))).format('DD MMM YYYY') : '—');

const List = () => {

    const [isLoader, setIsLoader] = useState(false);
    const [codes, setCodes] = useState([]);
    const [pagination, setPagination] = useState({});
    const [apiData, setApiData] = useState({
        search: "",
        status: "",
        page: 1,
    });

    useEffect(() => {
        getCodes();
    }, [apiData.page, apiData.search, apiData.status]);

    const getCodes = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/promo-codes",
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                setCodes(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
        }
    };

    const handlePageChange = (event) => {
        setApiData(prev => ({ ...prev, page: event }));
    };

    const clearFilter = () => {
        setApiData({ search: '', status: '', page: 1 });
    };

    const delData = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'you will not be able to revert it!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            const response = await ApiService.request({
                method: "DELETE",
                url: `admin/promo-codes/${id}`,
            });
            const data = response.data;
            if (data.status) {
                getCodes();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        }
    };

    const updateStatus = async (id) => {
        try {
            const response = await ApiService.request({
                method: "POST",
                url: `admin/promo-codes/status/${id}`,
            });
            const data = response.data;
            if (data.status) {
                getCodes();
                toast.success(data.message);
                return true;
            }
            toast.error(data.message);
            return false;
        } catch {
            toast.error('Failed to update status');
            return false;
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Promo Codes</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Promo Codes</li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to={`/admin/promo-codes/create/`} className="btn btn-sm btn-base">
                            Add New
                        </Link>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <form method="get">
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="d-flex align-items-center position-relative my-1">
                                                <input
                                                    type="text"
                                                    value={apiData.search}
                                                    onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })}
                                                    className="form-control form-control-solid me-5"
                                                    placeholder="Search code or title.."
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <select
                                                className="form-select form-select-solid my-1"
                                                value={apiData.status}
                                                onChange={e => setApiData({ ...apiData, status: e.target.value, page: 1 })}
                                            >
                                                <option value="">All Statuses</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3 mt-2">
                                            <button
                                                type="button"
                                                onClick={clearFilter}
                                                className={`btn btn-sm btn-secondary mx-1 ${!apiData.search && !apiData.status ? 'd-none' : ''}`}
                                            >
                                                clear
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th>Code</th>
                                                <th>Discount</th>
                                                <th>Users</th>
                                                <th>Usage</th>
                                                <th>Validity</th>
                                                <th>Used</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {codes.length > 0 ? codes.map((promo) => (
                                                <tr key={promo._id}>
                                                    <td>
                                                        <Link to={`/admin/promo-codes/${promo._id}`} className="text-dark fw-bold text-hover-primary">{promo.code}</Link>
                                                        {promo.title && (
                                                            <div className="text-muted fs-7">{promo.title}</div>
                                                        )}
                                                    </td>
                                                    <td>{formatDiscount(promo)}</td>
                                                    <td>
                                                        {promo.user_scope === 'specific' ? (
                                                            <span className="badge badge-light-warning">{promo.user_ids?.length || 0} specific</span>
                                                        ) : (
                                                            <span className="badge badge-light-info">Anyone</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {promo.usage_type === 'single'
                                                            ? 'Once per user'
                                                            : promo.per_user_limit != null
                                                                ? `${promo.per_user_limit}× per user`
                                                                : 'Unlimited per user'}
                                                        {promo.total_usage_limit != null && (
                                                            <div className="text-muted fs-7">{promo.total_usage_limit} total</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="fs-7">{formatDate(promo.start_date)} → {formatDate(promo.expiry_date)}</div>
                                                        {validityBadge(promo)}
                                                    </td>
                                                    <td>
                                                        <Link
                                                            to={`/admin/promo-codes/${promo._id}`}
                                                            className="badge badge-light-primary"
                                                        >
                                                            {promo.used_count || 0}
                                                            {promo.total_usage_limit != null ? ` / ${promo.total_usage_limit}` : ''}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <StatusToggle
                                                            checked={promo.status}
                                                            onToggle={() => updateStatus(promo._id)}
                                                        />
                                                    </td>
                                                    <td className="mt-2 text-nowrap">
                                                        <Link to={`/admin/promo-codes/${promo._id}`} className="btn btn-sm h-35px btn-light-info me-2" title="View details">
                                                            <i className="bi bi-eye-fill fs-6"></i>
                                                        </Link>
                                                        <Link to={`/admin/promo-codes/create/${promo._id}`} className="btn btn-sm h-35px btn-light-primary">
                                                            <i className="bi bi-pencil-fill fs-6"></i>
                                                        </Link>
                                                        <button onClick={() => delData(promo._id)} className="btn btn-sm h-35px btn-light-danger ms-2">
                                                            <i className="bi bi-trash-fill fs-6"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="8" className="text-dark text-center mt-2">No data found!!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center" />
                                    )}

                                    {pagination.total_pages > 1 && (
                                        <Paginator
                                            page={pagination.current_page}
                                            rows={pagination.per_page}
                                            totalRecords={pagination.total_records}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
